import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useEffect, useState, useRef, useCallback } from "react";
import { ListLetter, DeleteLetter } from "../../services/letter";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import OptionMenu from "../../components/Menu";
import jalaali from "jalaali-js";

function LetterList() {
  const navigate = useNavigate();

  const [letters, setLetters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef(null);

  // -----------------------------
  // Fetch Letters
  // -----------------------------
  const fetchData = useCallback(async (pageid = 1, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const response = await ListLetter(pageid);
      const responseData = response.data || {};
      const newLetters = responseData.listLetter || [];
      const pagination = responseData.pagination || {};

      if (append) {
        setLetters((prev) => [...prev, ...newLetters]);
      } else {
        setLetters(newLetters);
      }

      // Check if there are more pages
      const hasMorePages = pagination.currentPage < pagination.pageCount && newLetters.length > 0;
      setHasMore(hasMorePages);
      setCurrentPage(pagination.currentPage || pageid);

      setIsLoading(false);
      setIsLoadingMore(false);
    } catch (error) {
      console.error("Error fetching letters:", error);
      if (!append) {
        setLetters([]);
      }
      setIsLoading(false);
      setIsLoadingMore(false);
      setHasMore(false);
    }
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    // Only set up observer if we have letters and might have more
    if (isLoading || letters.length === 0) {
      return;
    }

    const currentTarget = observerTarget.current;
    if (!currentTarget) {
      console.log("Observer target not found");
      return;
    }

    console.log("Setting up IntersectionObserver - hasMore:", hasMore, "currentPage:", currentPage);

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        console.log("IntersectionObserver entry:", {
          isIntersecting: entry.isIntersecting,
          hasMore,
          isLoadingMore,
          isLoading,
          currentPage
        });
        
        if (entry.isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          console.log("Triggering loadMore - Loading page:", currentPage + 1);
          fetchData(currentPage + 1, true);
        }
      },
      { 
        threshold: 0.1,
        rootMargin: "300px" // Start loading 300px before reaching the bottom
      }
    );

    observer.observe(currentTarget);
    console.log("Observer attached to target");

    return () => {
      console.log("Cleaning up observer");
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, isLoading, currentPage, fetchData, letters.length]);

  // Initial load
  useEffect(() => {
    fetchData(1, false);
  }, []);

  // -----------------------------
  // Actions
  // -----------------------------
  const handleDownload = async (id) => {
    try {
      window.location.href = `https://api.copysa.ir/api/Letter/DownloadLetter/${id}`;
    } catch (err) {
      Swal.fire({
        title: "خطا در دریافت اطلاعات",
        confirmButtonText: "باشه",
      });
    }
  };

  const handleEdit = (letter) => {
    navigate("/addletter", {
      state: { letterId: letter.idletterstr, mode: "edit" },
    });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "  حذف  نامه ",
      text: "آیا از حذف این نامه اطمینان دارید؟",
      icon: "warning",
      showCancelButton: true,

      confirmButtonText: " حذف ",
      cancelButtonText: "انصراف",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await DeleteLetter(id);
          Swal.fire("حذف شد!", "نامه با موفقیت حذف شد.", "success");
          // Reset pagination and reload from first page
          setCurrentPage(1);
          setHasMore(true);
          fetchData(1, false);
        } catch (error) {
          console.error("Error deleting letter:", error);
          Swal.fire("خطا!", "مشکلی در حذف نامه رخ داد.", "error");
        }
      }
    });
  };

  // -----------------------------
  // Loading State
  // -----------------------------
  if (isLoading)
    return (
      <Box
        sx={{
          height: "50vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="primary" size={70} />
      </Box>
    );

  // -----------------------------
  // Empty State
  // -----------------------------
  if (!letters || letters.length === 0) {
    return (
      <Box className="flex flex-col gap-3 mb-24">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            my: 4,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
            هیچ نامه‌ای وجود ندارد
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            برای شروع، اولین نامه خود را اضافه کنید
          </Typography>
          <Button
            variant="text"
            color="primary"
            onClick={() => navigate("/addletter")}
          >
            <Typography variant="h6">افزودن نامه</Typography>
          </Button>
        </Paper>
        <Box
          sx={{
            width: "100%",
            position: "fixed",
            left: 0,
            right: 0,
            bottom: "6.5rem",
            display: "flex",
            justifyContent: "start",
            px: 3,
            zIndex: 12,
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate("/addletter")}
            sx={{
              padding: "15px 20px",
              borderRadius: "15px",
              border: "1px solid white",
              textTransform: "none",
            }}
          >
            <Typography variant="h5" sx={{ marginRight: "10px" }}>
              افزودن نامه
            </Typography>
            <AddIcon size="large" />
          </Button>
        </Box>
      </Box>
    );
  }

  // -----------------------------
  // Helper function to truncate text
  // -----------------------------
  const truncateText = (text, maxLength = 80) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // -----------------------------
  // Helper function to convert date to Jalali
  // -----------------------------
  const formatJalaliDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      // Try to parse the date string
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString; // Return original if invalid
      }
      
      // Convert to Jalali
      const jalali = jalaali.toJalaali(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate()
      );
      
      // Format as YYYY/MM/DD
      return `${jalali.jy}/${String(jalali.jm).padStart(2, "0")}/${String(jalali.jd).padStart(2, "0")}`;
    } catch (error) {
      console.error("Error converting date to Jalali:", error);
      return dateString; // Return original on error
    }
  };

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <Box className="flex flex-col mb-24" sx={{ width: "100%", p: 1 }}>
      {letters.map((letter) => (
        <Card
          key={letter.idletterstr}
          elevation={0}
          sx={{
            mb: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "primary.light",
            backgroundColor: "action.hover",
            cursor: "pointer",
            transition: "all 0.2s ease",
            position: "relative",
            overflow: "visible",
        
          
          }}
          onClick={() => handleEdit(letter)}
        >
          <CardHeader
            title={letter.title}
            titleTypographyProps={{
              variant: "subtitle1",
              sx: {
                fontWeight: 600,
                px: 0,
                color: "text.primary",
                fontSize: { xs: "0.938rem", sm: "1rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
            subheader={
              <Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
                  {letter.sender || letter.Sender ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        color: "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      }}
                    >
                      از: {letter.sender || letter.Sender}
                    </Typography>
                  ) : null}
                  {letter.receiver || letter.Receiver ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        color: "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      }}
                    >
                      به: {letter.receiver || letter.Receiver}
                    </Typography>
                  ) : null}
                </Box>
                {letter.dateletter && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      mt: 0.5,
                      display: "block",
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    }}
                  >
                    {formatJalaliDate(letter.dateletter)}
                  </Typography>
                )}
              </Box>
            }
            action={
              <Box onClick={(e) => e.stopPropagation()}>
                <OptionMenu
                  iconButtonProps={{
                    size: "medium",
                    sx: {
                      color: "text.secondary",
                      "&:hover": {
                        backgroundColor: "action.selected",
                        color: "text.primary",
                      },
                      transition: "all 0.2s ease",
                    },
                  }}
                  iconClassName="text-secondary"
                  options={[
                    {
                      text: "ویرایش",
                      icon: <EditIcon fontSize="small" />,
                      menuItemProps: {
                        onClick: () => handleEdit(letter),
                      },
                    },
                    {
                      text: "حذف",
                      icon: <DeleteIcon fontSize="small" />,
                      menuItemProps: {
                        onClick: () => handleDelete(letter.idletterstr),
                      },
                    },
                    {
                      text: "دانلود",
                      icon: <DownloadIcon fontSize="small" />,
                      menuItemProps: {
                        onClick: () => handleDownload(letter.idletterstr),
                      },
                    },
                  ]}
                />
              </Box>
            }
           
          />
          <CardContent
            sx={{
              p: 0,
              px: 1,
              "&:last-child": {
                paddingBottom: 2,
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.813rem", sm: "0.875rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: 1.5,
              }}
            >
              {truncateText(letter.textletter, 250)}
            </Typography>
          </CardContent>
        </Card>
      ))}
      
      {/* Infinite scroll observer target */}
      {letters.length > 0 && (
        <Box
          ref={observerTarget}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isLoadingMore && <CircularProgress size={40} color="primary" />}
        
        </Box>
      )}
      
      <Box
        sx={{
          width: "100%",
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "6.5rem", // Place above nav
          display: "flex",
          justifyContent: "start",
          px: 3,
          zIndex: 12,
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate("/addletter")}
          sx={{
            padding: "15px 20px",
            borderRadius: "15px",
            border: "1px solid white",
            textTransform: "none",
          }}
        >
          <Typography variant="h5" sx={{ marginRight: "10px" }}>
            افزودن نامه
          </Typography>
          <AddIcon size="large" />
        </Button>
      </Box>
    </Box>
  );
}

export default LetterList;
